import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { BASE_URL } from "../../redux/ApiEndPoints";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export default function StudentHoursSpent() {
  const auth = useSelector((state) => state.auth);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchLearningProgress = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}student/learning-progress`, {
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setChartData(data.data);
          }
        }
      } catch (error) {
        console.error("Error fetching learning progress:", error);
      } finally {
        setLoading(false);
      }
    };

    if (auth.token) {
      fetchLearningProgress();
    }
  }, [auth.token]);

  if (loading) {
    return (
      <article className="hours-spent-card">
        <div
          className="bar-holder d-flex justify-content-center align-items-center"
          style={{ height: "300px" }}
        >
          <div className="dottedLoading theme-color"></div>
        </div>
      </article>
    );
  }

  if (!chartData || chartData?.length === 0) {
    return (
      <article className="hours-spent-card">
        <div
          className="bar-holder d-flex justify-content-center align-items-center"
          style={{ height: "300px" }}
        >
          <div className="text-center">
            <h5 className="text-muted">No learning data</h5>
            <p className="text-muted small">
              Start enrolling in courses to see your progress
            </p>
          </div>
        </div>
      </article>
    );
  }

  const stackedValues = chartData?.map(
    (item) => item.coursesStarted + item.coursesCompleted
  );
  const maxStackedValue = Math.max(...stackedValues);
  const dynamicMax = Math.max(5, Math.ceil(maxStackedValue * 1.2));
  console.log("maxStackedValue", maxStackedValue);
  console.log("dynamicMax", dynamicMax);
  const data = {
    labels: chartData?.map((item) => item.month),
    datasets: [
      {
        label: "Courses Started",
        data: chartData?.map((item) => item.coursesStarted),
        backgroundColor: "#F05203",
        stack: "combined",
        borderRadius: 20,
        barThickness: windowWidth < 600 ? 30 : windowWidth < 900 ? 40 : 50,
      },
      {
        label: "Courses Completed",
        data: chartData?.map((item) => item.coursesCompleted),
        backgroundColor: "#EBEBEB",
        stack: "combined",
        borderRadius: 20,
        barThickness: windowWidth < 600 ? 30 : windowWidth < 900 ? 40 : 50,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y} Course${
              context.parsed.y !== 1 ? "s" : ""
            }`;
          },
        },
        titleFont: { size: windowWidth < 600 ? 12 : 14 },
        bodyFont: { size: windowWidth < 600 ? 10 : 12 },
      },
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          padding: 5,
          font: {
            size: windowWidth < 600 ? 10 : 12,
          },
        },
        grid: {
          display: true,
          drawOnChartArea: false,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        max: dynamicMax,
        ticks: {
          stepSize: Math.ceil(dynamicMax / 5),
          callback: function (value) {
            return value + " Course" + (value !== 1 ? "s" : "");
          },
          font: {
            size: windowWidth < 600 ? 10 : 12,
          },
        },
        grid: {
          drawTicks: false,
        },
      },
    },
    categoryPercentage: 0.8,
    barPercentage: 0.9,
  };

  return (
    <>
      <div className="graph-labels d-flex flex-wrap gap-2 mb-15 mb-sm-25 mb-md-35 mb-lg-42">
        <div className="d-flex me-20">
          <div className="label-box label-theme me-10"></div>
          <span className="text-black ah-text-sm fw-semibold">
            Courses Started
          </span>
        </div>
        <div className="d-flex me-20">
          <div className="label-box label-gray me-10"></div>
          <span className="text-black ah-text-sm fw-semibold">
            Courses Completed
          </span>
        </div>
      </div>

      <article className="hours-spent-card">
        <div className="bar-holder">
          <Bar data={data} options={options} />
        </div>
      </article>
    </>
  );
}
